import { useState, useRef, useEffect } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Plus, Trash2, Eye, EyeOff, Check, X, Loader2, AlertCircle, Pencil } from "lucide-react"
import { adminApiKeysApi, adminSystemApi, type ApiKeyDTO , type AiPricesDTO } from "@/lib/api"
import { toast } from "sonner"

type Provider = 'openai' | 'google' | 'vertex_ai'
type ApiUsage = 'vision' | 'generation'

const PROVIDERS: { id: Provider; label: string }[] = [
  { id: 'openai',    label: 'OpenAI'    },
  { id: 'google',    label: 'Google'    },
  { id: 'vertex_ai', label: 'Vertex AI' },
]

const USAGES: { id: ApiUsage; label: string }[] = [
  { id: 'vision',     label: 'Vision'     },
  { id: 'generation', label: 'Generation' },
]

function parseUsages(raw: string): ApiUsage[] {
  return (raw ?? '').split(',').filter((v): v is ApiUsage => v === 'vision' || v === 'generation')
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!on)} style={{ width:36, height:20, borderRadius:10, border:'none', background: on ? 'oklch(0.145 0 0)' : 'oklch(0.84 0 0)', position:'relative', cursor:'pointer', transition:'background 0.2s', flexShrink:0, padding:0 }}>
      <span style={{ position:'absolute', top:2, left: on ? 18 : 2, width:16, height:16, borderRadius:'50%', background:'white', transition:'left 0.18s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  )
}

type TestState = { status: 'idle' | 'testing' | 'ok' | 'error'; message: string; models: string[] }

function AddKeyForm({ initial, isEdit, onSave, onCancel }: {
  initial?: { provider: Provider; name: string; model_vision: string | null; model_generation: string | null; usages?: ApiUsage[] }
  isEdit?: boolean
  onSave: (provider: Provider, name: string, key: string, modelVision: string | null, modelGen: string | null, usages: ApiUsage[]) => void
  onCancel: () => void
}) {
  const [provider, setProvider]           = useState<Provider>(initial?.provider ?? 'openai')
  const [name, setName]                   = useState(initial?.name ?? '')
  const [usages, setUsages]               = useState<ApiUsage[]>(initial?.usages ?? ['vision'])
  const [key, setKey]                     = useState('')
  const [showKey, setShowKey]             = useState(false)
  const [fileName, setFileName]           = useState('')
  const [modelVision, setModelVision] = useState(initial?.model_vision ?? '')
  const [modelGen, setModelGen]       = useState(initial?.model_generation ?? '')
  const [test, setTest]               = useState<TestState>({ status: isEdit ? 'ok' : 'idle', message: isEdit ? 'Previously validated' : '', models: [] })
  const fileRef = useRef<HTMLInputElement>(null)

  const isVertex = provider === 'vertex_ai'
  const valid = name.trim() && usages.length > 0 && test.status === 'ok' && (isEdit ? true : !!key.trim())

  const toggleUsage = (u: ApiUsage) =>
    setUsages(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u])

  const handleJsonFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = ev => { setKey(ev.target?.result as string ?? ''); setTest({ status: 'idle', message: '', models: [] }) }
    reader.readAsText(file)
  }

  const handleTest = async () => {
    if (!key.trim()) return
    setTest({ status: 'testing', message: '', models: [] })
    try {
      const r = await adminApiKeysApi.test({ provider, key })
      setTest({ status: r.ok ? 'ok' : 'error', message: r.message, models: [] })
    } catch {
      setTest({ status: 'error', message: 'Request failed', models: [] })
    }
  }

  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl border border-border bg-secondary/20">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">Name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Vertex AI prod"
          className="h-8 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/30" />
      </div>
      <div className="flex gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Provider</label>
          <div className="flex gap-2">
            {PROVIDERS.map(p => (
              <button key={p.id} onClick={() => { setProvider(p.id); setKey(''); setFileName(''); setTest({ status: 'idle', message: '', models: [] }) }}
                className={["px-3 h-7 rounded-lg border text-xs font-medium transition-colors",
                  provider === p.id ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"].join(' ')}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Usage</label>
          <div className="flex gap-2">
            {USAGES.map(u => (
              <button key={u.id} onClick={() => toggleUsage(u.id)}
                className={["px-3 h-7 rounded-lg border text-xs font-medium transition-colors",
                  usages.includes(u.id) ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"].join(' ')}>
                {u.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {isVertex ? (
          <>
            <label className="text-xs font-medium text-muted-foreground">Service Account JSON</label>
            <div className="flex gap-2 items-center">
              <button onClick={() => fileRef.current?.click()}
                className="h-8 px-3 rounded-lg border border-border bg-background text-sm text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors truncate max-w-xs">
                {fileName || 'Choose .json file…'}
              </button>
              {key && <Check className="w-4 h-4 text-green-600 shrink-0" />}
              <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={handleJsonFile} />
            </div>
            <p className="text-xs text-muted-foreground/60">GCP Console → IAM → Service Accounts → Keys → Add Key</p>
          </>
        ) : (
          <>
            <label className="text-xs font-medium text-muted-foreground">API Key</label>
            <div className="flex gap-2">
              <input type={showKey ? 'text' : 'password'} value={key} onChange={e => { setKey(e.target.value); setTest({ status: 'idle', message: '', models: [] }) }}
                placeholder={provider === 'google' ? 'AIza...' : 'sk-prod-...'}
                className="flex-1 h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30" />
              <button onClick={() => setShowKey(v => !v)} className="px-2 text-muted-foreground hover:text-foreground transition-colors">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Test panel */}
      <div className="flex flex-col gap-2">
        <Button size="sm" variant="outline" disabled={!key.trim() || test.status === 'testing'} onClick={handleTest} className="w-fit">
          {test.status === 'testing' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
          Test connection
        </Button>
        {test.status !== 'idle' && (
          <div className={`flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${test.status === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : test.status === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-secondary'}`}>
            {test.status === 'ok' ? <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            {test.message}
          </div>
        )}
      </div>

      {/* Per-usage model inputs + system prompt — visible after test passes */}
      {test.status === 'ok' && (
        <div className="flex flex-col gap-3">
        <a href="https://ai.google.dev/gemini-api/docs/models/" target="_blank" rel="noreferrer"
           className="text-xs text-muted-foreground underline hover:text-foreground w-fit">
          Browse Gemini models ↗
        </a>
        <div className="flex gap-3">
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-xs font-medium text-muted-foreground">Model Vision</label>
            <input
              value={modelVision}
              onChange={e => setModelVision(e.target.value)}
              placeholder="e.g. gemini-2.0-flash"
              className="h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-xs font-medium text-muted-foreground">Model Generation</label>
            <input
              value={modelGen}
              onChange={e => setModelGen(e.target.value)}
              placeholder="e.g. gemini-2.0-flash"
              className="h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30"
            />
          </div>
        </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button size="sm" disabled={!valid} onClick={() => onSave(provider, name, key, modelVision || null, modelGen || null, usages)}>
          <Check className="w-3.5 h-3.5 mr-1" /> Save
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  )
}

export function APISettings() {
  const [keys, setKeys]         = useState<ApiKeyDTO[]>([])
  const [adding, setAdding]     = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    adminApiKeysApi.list().then(setKeys).finally(() => setLoading(false))
  }, [])

  const handleAdd = async (provider: Provider, name: string, key: string, modelVision: string | null, modelGen: string | null, usages: ApiUsage[]) => {
    try {
      const created = await adminApiKeysApi.create({ id: provider, name, key, usage: usages.join(','), model_vision: modelVision, model_generation: modelGen })
      setKeys(prev => [...prev, created])
      setAdding(false)
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to add key") }
  }

  const handleEdit = async (id: string, _provider: Provider, name: string, key: string, modelVision: string | null, modelGen: string | null, usages: ApiUsage[]) => {
    try {
      const updated = await adminApiKeysApi.update(id, { name, ...(key ? { key } : {}), usage: usages.join(','), model_vision: modelVision, model_generation: modelGen })
      setKeys(prev => prev.map(k => k.id === id ? updated : k))
      setEditingId(null)
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to update key") }
  }

  const handleToggle = async (id: string, is_active: boolean) => {
    try {
      const updated = await adminApiKeysApi.update(id, { is_active })
      setKeys(prev => prev.map(k => k.id === id ? updated : k))
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to toggle key") }
  }

  const handleDelete = async (id: string) => {
    try {
      await adminApiKeysApi.remove(id)
      setKeys(prev => prev.filter(k => k.id !== id))
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to delete key") }
  }

  if (loading) return <div className="text-sm text-muted-foreground p-4">Loading…</div>

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">API Settings</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manage API keys used as fallback for clients without their own</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} disabled={adding}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add key
        </Button>
      </div>

      {adding && (
        <AddKeyForm onSave={handleAdd} onCancel={() => setAdding(false)} />
      )}

      {keys.length === 0 && !adding ? (
        <div className="text-center py-10 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
          No API keys yet
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {keys.map(k => (
            <Card key={k.id} className={`transition-opacity ${k.is_active ? '' : 'opacity-55'}`}>
              {editingId === k.id ? (
                <div className="p-5">
                  <AddKeyForm
                    isEdit
                    initial={{ provider: k.id as Provider, name: k.name, model_vision: k.model_vision ?? null, model_generation: k.model_generation ?? null, usages: parseUsages(k.usage ?? 'vision') }}
                    onSave={(prov, name, key, mv, mg, usages) => handleEdit(k.id, prov, name, key, mv, mg, usages)}
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              ) : (
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex flex-col gap-0.5">
                      <div className="text-sm font-semibold">{k.name}</div>
                      <div className="text-xs text-muted-foreground">{k.id}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Toggle on={k.is_active} onChange={v => handleToggle(k.id, v)} />
                      <Button size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-foreground" onClick={() => setEditingId(k.id)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(k.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {parseUsages(k.usage ?? 'vision').map(u => (
                      <span key={u} className="text-[10px] font-medium px-1.5 py-0.5 rounded border border-border text-muted-foreground">
                        {u === 'generation' ? 'Generation' : 'Vision'}
                      </span>
                    ))}
                    {k.model_vision && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">V: {k.model_vision}</span>
                    )}
                    {k.model_generation && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">G: {k.model_generation}</span>
                    )}
                  </div>
                  <div className="h-8 bg-secondary border border-border rounded-lg px-3 flex items-center text-xs font-mono text-foreground/60">
                    {k.key_hint ?? '••••••••••••••••'}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <PricesCard />
    </div>
  )
}


function PricesCard() {
  const [prices, setPrices] = useState<AiPricesDTO>({})
  const [newModel, setNewModel] = useState('')
  const [dirty, setDirty] = useState(false)

  useEffect(() => { adminSystemApi.getPrices().then(setPrices).catch(() => {}) }, [])

  const entries = Object.entries(prices).filter(([, v]) => typeof v === 'object' && v !== null) as [string, { input_eur_per_mtok?: number; output_eur_per_mtok?: number }][]
  const discount = typeof prices.batch_discount === 'number' ? prices.batch_discount : 0.5

  const setField = (model: string, field: 'input_eur_per_mtok' | 'output_eur_per_mtok', v: string) => {
    setPrices(prev => ({ ...prev, [model]: { ...(prev[model] as object ?? {}), [field]: v === '' ? undefined : Number(v) } }))
    setDirty(true)
  }
  const save = async () => {
    try { await adminSystemApi.setPrices(prices); setDirty(false); toast.success("Prices saved") }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed to save prices") }
  }

  return (
    <Card className="p-5 flex flex-col gap-4">
      <div>
        <div className="text-sm font-semibold">AI prices (€)</div>
        <p className="text-xs text-muted-foreground mt-0.5">Per-model € per 1M tokens — used by the cost tracker. Batch jobs get the discount below.</p>
      </div>
      {entries.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[1fr_110px_110px_28px] gap-2 text-[10px] text-muted-foreground uppercase tracking-wide">
            <span>provider:model</span><span>€/1M input</span><span>€/1M output</span><span />
          </div>
          {entries.map(([model, v]) => (
            <div key={model} className="grid grid-cols-[1fr_110px_110px_28px] gap-2 items-center">
              <span className="text-xs font-mono truncate">{model}</span>
              <input type="number" step="0.01" value={v.input_eur_per_mtok ?? ''} onChange={e => setField(model, 'input_eur_per_mtok', e.target.value)}
                className="h-8 rounded-lg border bg-background px-2 text-xs font-mono" />
              <input type="number" step="0.01" value={v.output_eur_per_mtok ?? ''} onChange={e => setField(model, 'output_eur_per_mtok', e.target.value)}
                className="h-8 rounded-lg border bg-background px-2 text-xs font-mono" />
              <button onClick={() => { setPrices(prev => { const n = { ...prev }; delete n[model]; return n }); setDirty(true) }}
                className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input value={newModel} onChange={e => setNewModel(e.target.value)} placeholder="provider:model, or model alone for all providers  e.g. gemini-3.5-flash"
          className="h-8 flex-1 rounded-lg border bg-background px-2 text-xs font-mono" />
        <Button size="sm" variant="outline" disabled={!newModel.trim()}
          onClick={() => { setPrices(prev => ({ ...prev, [newModel.trim()]: {} })); setNewModel(''); setDirty(true) }}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add model
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-xs text-muted-foreground">Batch discount</label>
        <input type="number" step="0.05" min="0" max="1" value={discount}
          onChange={e => { setPrices(prev => ({ ...prev, batch_discount: Number(e.target.value) })); setDirty(true) }}
          className="h-8 w-20 rounded-lg border bg-background px-2 text-xs font-mono" />
        <span className="text-[11px] text-muted-foreground">×cost for batch jobs (0.5 = −50%)</span>
        <Button size="sm" className="ml-auto" onClick={save} disabled={!dirty}>Save prices</Button>
      </div>
    </Card>
  )
}
